import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectProductDetail,
  selectEditingVariantItem,
} from "../../redux/features/restaurants/restaurantSelectors";
import { convertToNumberKWD } from "../../utils/common";
import ProductVariantSelection from "./productVariantSelection";
import { generateVariantId } from "../../utils/menu";
import {
  setCartVariantItems,
  setShowProductDetail,
  updateCartVariantItem,
  setEditingVariantItem,
  setCateringCartItems,
  setCateringCartVariantItems,
  updateCateringCartVariantItem,
  addCateringCartVariantItem,
  updateVariantItemInCateringPackage,
  addVariantItemToCateringPackage,

} from "../../redux/features/restaurants/restaurantSlice";

const Product = () => {
  const productDetail = useSelector(selectProductDetail);
  const editingItem = useSelector(selectEditingVariantItem);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [isError, setIsError] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalVariantId, setOriginalVariantId] = useState(null);

  // Use this ref to track when product detail has loaded after edit mode is set
  const productLoadedAfterEditRef = useRef(false);

  const dispatch = useDispatch();
  
  // Use editingItem.productInfo if available, otherwise fall back to productDetail
  const effectiveProductDetail = editingItem?.productInfo || productDetail;
  const basePrice = convertToNumberKWD(effectiveProductDetail?.price || 0);
  


  // Initialize component when editing an existing variant item
  useEffect(() => {
    if (editingItem && editingItem.variantInfo) {
      setIsEditMode(true);
      setQuantity(editingItem.variantInfo.quantity);
      setOriginalVariantId(editingItem.variantInfo.variantId);
      setInstructions(editingItem.variantInfo.instructions || "");

      // Only pre-set the selections if product details are loaded
      if (effectiveProductDetail) {
        // This ensures we have the product details before setting selections
        setSelectedVariants(editingItem.variantInfo.selectedVariant);
        setIsValid(true);
        productLoadedAfterEditRef.current = true;
      }
    } else {
      setIsEditMode(false);
      setQuantity(1);
      setInstructions("");
      setOriginalVariantId(null);
      productLoadedAfterEditRef.current = false;
    }
  }, [editingItem, effectiveProductDetail]);

  // Effect to handle when product details load after edit mode is set
  useEffect(() => {
    if (
      isEditMode &&
      effectiveProductDetail &&
      !productLoadedAfterEditRef.current &&
      editingItem?.variantInfo
    ) {
      // Product detail loaded after edit mode was set, now we can set the selections
      setSelectedVariants(editingItem.variantInfo.selectedVariant);
      setIsValid(true);
      productLoadedAfterEditRef.current = true;
    }
  }, [effectiveProductDetail, isEditMode, editingItem]);

  // Calculate total price including variants
  const totalPrice = useMemo(() => {
    // Start with base price
    let total = basePrice;

    // Add price of selected variant options
    if (selectedVariants && Array.isArray(selectedVariants)) {
      selectedVariants.forEach((variant) => {
        if (variant.options && Array.isArray(variant.options)) {
          variant.options.forEach((option) => {
            // Add price for each selected option
            total += option.unit_price * option.option_quantity;
          });
        }
      });
    }

    return total;
  }, [basePrice, selectedVariants]);

  const handleInputChange = (event) => {
    setInstructions(event.target.value);
  };

  const handleAddToCart = () => {
    if (isValid) {
      const variantId = generateVariantId(selectedVariants, instructions);

      // Determine if this is a catering item by checking if editingItem has catering-specific properties
      // or if we're in a catering context
      const isCateringItem = editingItem?.productInfo?.is_catering || 
                             editingItem?.productInfo?.catering_item || 
                             effectiveProductDetail?.is_catering ||
                             effectiveProductDetail?.catering_item;

      if (isEditMode && editingItem) {
        // Update existing item
        const updatedVariantItem = {
          productInfo: editingItem.productInfo,
          variantInfo: {
            ...editingItem.variantInfo,
            selectedVariant: selectedVariants,
            quantity: quantity,
            instructions: instructions,
            variantId: variantId,
          },
          originalVariantId: originalVariantId,
        };

        if (isCateringItem) {
          // Update existing catering variant item inside active package
          const packageId = editingItem?.productInfo?.packageId || editingItem?.packageId;
          if (packageId) {
            dispatch(updateVariantItemInCateringPackage({ packageId, ...updatedVariantItem }));
          } else {
            // For non-package catering items, we need a packageId. Create a default one or skip.
            console.warn('updateCateringCartVariantItem now requires packageId. Consider using a package-based approach.');
          }
        } else {
          dispatch(updateCartVariantItem(updatedVariantItem));
        }
      } else {
        // Add new item
        if (isCateringItem) {
          // Add to catering cart
          const packageId = editingItem?.productInfo?.packageId || editingItem?.packageId;
          if (packageId) {
            dispatch(addVariantItemToCateringPackage({
              packageId,
              productId: effectiveProductDetail.id,
              variantId,
              productMeta: {
                name: effectiveProductDetail.name_en,
                name_en: effectiveProductDetail.name_en,
                price: effectiveProductDetail.price,
                category_id: effectiveProductDetail.category_id, // Preserve category_id for validation
              },
            }));
          } else {
            // For non-package catering items, we need a packageId. Create a default one or skip.
            console.warn('setCateringCartVariantItems now requires packageId. Consider using a package-based approach.');
          }
        } else {
          // Add to regular cart
          const variantItems = {
            id: effectiveProductDetail.id,
            name: effectiveProductDetail.name_en,
            price: effectiveProductDetail.price,
            has_variants: effectiveProductDetail.has_variants,
            image: effectiveProductDetail.image,
            description_en: effectiveProductDetail.description_en,
            items: [
              {
                variantId,
                selectedVariant: selectedVariants,
                quantity: quantity,
                instructions: instructions,
              },
            ],
          };
          dispatch(setCartVariantItems(variantItems));
        }
      }

      // Clear editing state and close modal
      dispatch(setEditingVariantItem(null));
      dispatch(setShowProductDetail(false));
    } else {
      setIsError(true);
    }
  };

  const handleCancel = () => {
    // Clear editing state and close modal
    dispatch(setEditingVariantItem(null));
    dispatch(setShowProductDetail(false));
  };

  useEffect(() => {
    if (isValid) {
      setIsError(false);
    }
  }, [isValid]);

  // If product detail is not loaded, show loading indicator
  if (!effectiveProductDetail) {
    return (
      <div className="sm:w-[447px] shadow bg-neutral-100 sm:border-l-2 sm:border-neutral-500 p-4 flex justify-center items-center h-screen">
        <p>Loading product details...</p>
      </div>
    );
  }

  return (
    <div className="fixed top-20 right-0 sm:w-[447px] shadow bg-neutral-100 p-4 flex flex-col h-[calc(100vh-5rem)] z-20">
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 border border-amber-100 w-full max-w-md bg-white rounded-md m-auto mt-5">
      <img
          alt="close icon"
          className="absolute -top-10 -right-12 cursor-pointer"
          src="/Images/Menu/closeIcon.svg"
          onClick={handleCancel}
          />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {/* Left Image Section - Always use effectiveProductDetail for display info */}
            <div className="relative">
              <img
                src={effectiveProductDetail.image}
                alt="Product"
                className="w-32 h-32 bg-[#e7ebf0] rounded-lg object-cover"
              />
              <div className="absolute bottom-[-8px] left-1/2 transform -translate-x-1/2 bg-amber-100 sm:w-[121px] h-[35px] text-white px-2 py-1 rounded-xl flex justify-center items-center">
                <p className="text-xs font-normal">KWD</p>
                <p className="text-xl font-bold">{basePrice.toFixed(3)}</p>
              </div>
            </div>

            {/* Text Section - Always use effectiveProductDetail for display info */}
            <div className="text-center sm:text-left">
              <div className="text-[22px] font-semibold mt-1">
                {effectiveProductDetail.name_en}
              </div>
              <p className="text-[10px] text-gray-500">
                {effectiveProductDetail.description_en}
              </p>
            </div>
          </div>

          <ProductVariantSelection
            variants={effectiveProductDetail?.variants || []}
            onValidityChange={setIsValid}
            errorShow={isError}
            setSelectedVariants={setSelectedVariants}
            initialSelections={isEditMode ? selectedVariants : null}
          />
        </div>
      </div>
      <textarea
        placeholder="Special Instructions (Optional)"
        className={`border border-amber-100 placeholder-gray-400 px-4 py-2 w-full mt-2 h-[144px] rounded-[5px]`}
        value={instructions}
        onChange={handleInputChange}
      />
      </div>

      {/* Fixed bottom section for quantity and add to cart */}
      <div className="flex items-center justify-between gap-4 mt-4 bg-neutral-100 py-2">
        {/* Quantity Selector Button */}
        <button className="flex items-center bg-white border border-amber-100 w-[132px] h-10 px-2 py-2 rounded-lg gap-2">
          <span className="text-amber-100 text-lg">Qty</span>
          <button
            className="flex justify-center items-center bg-white w-6 h-6 rounded-full border border-[#DCDFE3] leading-none"
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the parent button
              setQuantity(Math.max(1, quantity - 1));
            }}
          >
            <span className="inline-block" style={{ marginBottom: "2px" }}>
              -
            </span>
          </button>
          <span>{quantity}</span>
          <button
            className="flex justify-center items-center bg-white w-6 h-6 rounded-full border border-[#DCDFE3] leading-none"
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the parent button
              setQuantity(quantity + 1);
            }}
          >
            <span className="inline-block">+</span>
          </button>
        </button>

        {/* Add to Cart Button */}
        <div onClick={handleAddToCart} className="w-full sm:w-[222px]">
          <button className="flex items-center justify-between bg-amber-100 text-white px-4 py-2 rounded-lg gap-4 w-full sm:w-[222px] h-10">
            <span className="text-lg">
              {isEditMode ? "Update Cart" : "Add to Cart"}
            </span>
            <span className="text-xs">
              +KWD {(totalPrice * quantity).toFixed(3)}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Product;
